import "./CreativeList.scss";
import { ABCDCompanyOptions } from "@blisspointmedia/bpm-types/dist/pgTables/ABCDCompanyOptions";
import { ABCDCreative } from "@blisspointmedia/bpm-types/dist/ABCD";
import { awaitJSON, CreativeLambdaFetch } from "../../utils/fetch-utils";
import { DownloadToggle } from "../../Components/DownloadDropdown/DownloadToggle";
import { getGroupedTVADCreativeList } from "./TVADCreativeListUtils";
import { MediaType, useCreativeMap } from "../../redux/creative";
import { RadialScoreChart } from "../RadialScoreChart/RadialScoreChart";
import { RouteComponentProps } from "@reach/router";
import { useCompanyInfo } from "../../redux/company";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSetError } from "../../redux/modals";
import * as R from "ramda";
import * as UserRedux from "../../redux/user";
import AddCreativeModal from "../AddEditCreativeModal/AddCreativeModal";
import EditCreativeModal from "../EditCreativeModal/EditCreativeModal";
import RunABCDModal from "../RunABCDModal/RunABCDModal";
import useLocation from "../../utils/hooks/useLocation";
import {
  ABCDScoreObject,
  getABCDCompanyOptions,
  getABCDCreatives,
  getABCDScore,
  getS3Scores,
} from "../ABCD/ABCDUtils";
import {
  BPMTable,
  Button,
  ButtonFrameworkVariant,
  ButtonType,
  Dropdown,
  DropdownToggleType,
  FilterBar,
  IconToggleButton,
  Img,
} from "../../Components";
import {
  applyCreativeMapFilters,
  CREATIVE_MEDIATYPES_TYPES,
  CREATIVE_HEADERS,
  CreativeListMediaTypes,
  getABCDScoreContainer,
  getDescriptionContainer,
  getExpandedDetailsContainer,
  getMediatypeFilters,
  getThumbnail,
  VIEW_TYPES,
  View,
  VIEWS,
  getTableViewHeaders,
} from "./CreativeListUtils";
import {
  MdAdd,
  MdCancel,
  MdCheckBox,
  MdEdit,
  MdOutlineList,
  MdOutlineSquare,
  MdOutlineTableRows,
} from "react-icons/md";

const OVERALL_RADIAL_SIZE = 87;
const OVERALL_RADIAL_BAR_RATIO = 13 / OVERALL_RADIAL_SIZE;

const CreativeList = ({ path }: RouteComponentProps): JSX.Element => {
  const isInternal = useSelector(UserRedux.isInternalSelector);
  const [abcdCompanyOptions, setABCDCompanyOptions] = useState<ABCDCompanyOptions>();
  const [abcdCreativeSettingsMap, setABCDCreativeSettingsMap] = useState<
    Record<string, ABCDCreative>
  >();
  const [abcdScoreMap, setABCDScoreMap] = useState<Record<string, ABCDScoreObject>>();
  const [bulkEditCreatives, setBulkEditCreatives] = useState<Record<string, object>>({});
  const [bulkEditSelect, setBulkEditSelect] = useState<boolean>(false);
  const [creativeListFilter, setCreativeListFilter] = useState<{ filter: Function }>();
  const [creativeOptions, setCreativeOptions] = useState<{ categoryData: { name: string }[] }>();
  const [openABCDSection, setOpenABCDSection] = useState<string>("");
  const [selectedABCDCreative, setSelectedABCDCreative] = useState<string>();
  const [selectedCreativeKey, setSelectedCreativeKey] = useState<string>();
  const [selectedEditCreative, setSelectedEditCreative] = useState<object>();
  const [showTableView, setShowTableView] = useState<boolean>(false);
  const [selectedMediatype, setSelectedMediatype] = useState<CreativeListMediaTypes>(
    CREATIVE_MEDIATYPES_TYPES.LINEAR_STREAMING
  );
  const [selectedMediatypeFilters, setSelectedMediatypeFilters] = useState<
    Record<string, Record<string, boolean>>
  >({});
  const [selectedView, setSelectedView] = useState<View>(VIEWS[0]);
  const [showAddCreativeModal, setShowAddCreativeModal] = useState<boolean>(false);
  const { company } = useLocation();
  const companyInfo = useCompanyInfo();
  const setError = useSetError();
  const tvadMediatypes = useMemo(() => {
    if (selectedMediatype === CREATIVE_MEDIATYPES_TYPES.ALL_CREATIVES) {
      return ["streaming", "audio", "linear", "display"] as MediaType[];
    } else if (selectedMediatype[1]) {
      return ["streaming", "linear"] as MediaType[];
    } else if (selectedMediatype === CREATIVE_MEDIATYPES_TYPES.AUDIO) {
      return ["audio"] as MediaType[];
    } else if (selectedMediatype === CREATIVE_MEDIATYPES_TYPES.DISPLAY) {
      return ["display"] as MediaType[];
    }
    return [];
  }, [selectedMediatype]);

  const TVADCreativeMap = useCreativeMap({
    company: companyInfo.cid,
    mediaTypes: tvadMediatypes,
  });

  const creativeMap: Record<string, object> = useMemo(() => {
    const tvadMap = {};
    if (TVADCreativeMap && TVADCreativeMap.creativeMap) {
      for (const creativeID of R.keys(TVADCreativeMap.creativeMap)) {
        tvadMap[creativeID] = {
          ...TVADCreativeMap.creativeMap[creativeID],
          creativeListMediatype: selectedMediatype,
        };
      }
    }
    return {
      ...tvadMap,
    };
  }, [selectedMediatype, TVADCreativeMap]);

  const assetLabel = useMemo(() => (selectedMediatype === "Linear/Streaming" ? "ISCI" : "Asset"), [
    selectedMediatype,
  ]);

  const mediatypeFilters = useMemo(
    () =>
      getMediatypeFilters({
        mediatype: selectedMediatype,
        selectedToggles: selectedMediatypeFilters,
        selectedView,
        setSelectedToggles: setSelectedMediatypeFilters,
      }),
    [selectedMediatype, selectedMediatypeFilters, selectedView, setSelectedMediatypeFilters]
  );

  const creativeMapForFilterBar = useMemo(() => {
    const creativeMapForFilterBar = R.clone(creativeMap);
    if (R.isNil(abcdScoreMap)) {
      return creativeMapForFilterBar;
    }
    const features: Record<string, boolean> = {};
    for (const creativeID of R.keys(creativeMap)) {
      if (
        abcdScoreMap[creativeID] &&
        abcdScoreMap[creativeID].annotations_evaluation &&
        abcdScoreMap[creativeID].annotations_evaluation.evaluated_features
      ) {
        for (const feature of abcdScoreMap[creativeID].annotations_evaluation.evaluated_features) {
          features[feature.name] = true;
          creativeMapForFilterBar[creativeID][feature.name] = feature.detected;
        }
      }
    }
    for (const creativeID of R.keys(creativeMapForFilterBar)) {
      for (const feature of R.keys(features)) {
        creativeMapForFilterBar[creativeID][feature] = R.defaultTo(
          false,
          creativeMapForFilterBar[creativeID][feature]
        );
      }
    }
    return creativeMapForFilterBar;
  }, [abcdScoreMap, creativeMap]);

  // This variable should apply the config filters the user selects as well as filtering done from
  // the filter bar on the page.
  const filteredCreativeMap = useMemo(() => {
    const configFilteredCreativeMap = applyCreativeMapFilters({
      abcdScoreMap: R.defaultTo({}, abcdScoreMap),
      creativeMap: creativeMapForFilterBar,
      mediatype: selectedMediatype,
      selectedToggles: selectedMediatypeFilters,
    });
    if (creativeListFilter && creativeListFilter.filter) {
      const creativeMapWithFilterBarApplied = {};
      for (const creativeID of R.keys(configFilteredCreativeMap)) {
        if (creativeListFilter.filter(configFilteredCreativeMap[creativeID])) {
          creativeMapWithFilterBarApplied[creativeID] = configFilteredCreativeMap[creativeID];
        }
      }
      return creativeMapWithFilterBarApplied;
    }
    return configFilteredCreativeMap;
  }, [
    abcdScoreMap,
    creativeMapForFilterBar,
    creativeListFilter,
    selectedMediatype,
    selectedMediatypeFilters,
  ]);

  const filteredCreativeMapByKey: Record<string, object> = useMemo(() => {
    if (selectedView) {
      let tvadMap = {};
      for (const creativeID of R.keys(filteredCreativeMap)) {
        if (
          filteredCreativeMap[creativeID] &&
          (filteredCreativeMap[creativeID] as { creativeListMediatype: string })
            .creativeListMediatype
        ) {
          tvadMap[creativeID] = filteredCreativeMap[creativeID];
        }
      }
      tvadMap = getGroupedTVADCreativeList({
        abcdCreativeSettingsMap: R.defaultTo({}, abcdCreativeSettingsMap),
        creativeMap: filteredCreativeMap,
        selectedView,
      });
      return { ...tvadMap };
    }
    return filteredCreativeMap;
  }, [abcdCreativeSettingsMap, filteredCreativeMap, selectedView]);

  const { presentFeatures, totalFeatures } = useMemo(() => {
    let presentFeatures = 0;
    let totalFeatures = 0;
    if (abcdCreativeSettingsMap && abcdScoreMap) {
      for (const creativeID of R.keys(filteredCreativeMap)) {
        // Make sure we aren't double counting by checking that the file is the same as the creative we're looking at
        const creativeFilename = abcdCreativeSettingsMap[creativeID]
          ? abcdCreativeSettingsMap[creativeID].filename
          : "";
        if (!creativeFilename.includes(creativeID) || R.isNil(abcdScoreMap[creativeID])) {
          continue;
        }
        const scoreObject = getABCDScore(abcdScoreMap[creativeID]);
        presentFeatures += scoreObject.presentFeatures;
        totalFeatures += scoreObject.totalFeatures;
      }
    }
    return { presentFeatures, totalFeatures };
  }, [abcdCreativeSettingsMap, abcdScoreMap, filteredCreativeMap]);

  const overallTaggingScore = useMemo(
    () =>
      presentFeatures && totalFeatures ? (100.0 * presentFeatures) / totalFeatures : undefined,
    [presentFeatures, totalFeatures]
  );

  useEffect(() => {
    (async () => {
      if (R.isNil(abcdCompanyOptions)) {
        setABCDCompanyOptions(await getABCDCompanyOptions(company));
      }
    })();
  }, [abcdCompanyOptions, company]);

  useEffect(() => {
    (async () => {
      if (R.isNil(abcdCreativeSettingsMap)) {
        setABCDCreativeSettingsMap(await getABCDCreatives(company));
      }
    })();
  }, [abcdCreativeSettingsMap, company]);

  useEffect(() => {
    (async () => {
      if (R.isNil(abcdScoreMap) && !R.isNil(abcdCreativeSettingsMap)) {
        setABCDScoreMap(await getS3Scores(company, R.values(abcdCreativeSettingsMap)));
      }
    })();
  }, [abcdCreativeSettingsMap, abcdScoreMap, company]);

  useEffect(() => {
    if (R.isNil(creativeOptions)) {
      (async () => {
        try {
          const res = await CreativeLambdaFetch("/get_creative_options", {
            params: { cid: company },
          });
          const creativeOptions = await awaitJSON(res);
          setCreativeOptions(creativeOptions);
        } catch (e) {
          const reportError = e as Error;
          setError({ message: reportError.message, reportError });
        }
      })();
    }
  }, [company, creativeOptions, setError]);

  const runABCDModal = abcdCompanyOptions && abcdCreativeSettingsMap && selectedABCDCreative && (
    <RunABCDModal
      abcdCompanyOptions={abcdCompanyOptions}
      abcdCreativeMap={abcdCreativeSettingsMap}
      onHide={() => setSelectedABCDCreative(undefined)}
      selectedCreativeID={selectedABCDCreative}
    />
  );

  const addCreativeModal = showAddCreativeModal && (
    <AddCreativeModal
      abcdCompanyOptions={abcdCompanyOptions as ABCDCompanyOptions}
      mediatype={selectedMediatype}
      onHide={() => setShowAddCreativeModal(!showAddCreativeModal)}
    />
  );

  const editCreativeModal = selectedEditCreative && abcdCompanyOptions && (
    <EditCreativeModal
      abcdCompanyOptions={abcdCompanyOptions}
      abcdCreativeSettingsMap={R.defaultTo({}, abcdCreativeSettingsMap)}
      abcdScoreMap={R.defaultTo({}, abcdScoreMap)}
      bulkEditCreatives={R.values(bulkEditCreatives)}
      creative={selectedEditCreative}
      mediatype={selectedMediatype}
      onHide={() => {
        setSelectedEditCreative(undefined);
        setBulkEditCreatives({});
      }}
    />
  );

  const listView = !showTableView && (
    <div
      className={`listView ${
        !isInternal && selectedView === VIEW_TYPES.CREATIVE_VIEW ? "externalView" : ""
      }`}
    >
      <div className="creativeListWithOverallScore">
        <div className="creatives">
          {R.map(
            creativeKey => (
              <div
                className={`creativeContainer ${
                  selectedCreativeKey === creativeKey ? "selected" : ""
                }`}
                onClick={() => {
                  if (bulkEditSelect) {
                    const newBulkEditCreatives = R.clone(bulkEditCreatives);
                    if (newBulkEditCreatives[creativeKey]) {
                      delete newBulkEditCreatives[creativeKey];
                    } else {
                      // We should only allow select on creative IDs
                      newBulkEditCreatives[creativeKey] =
                        filteredCreativeMapByKey[creativeKey][`${"creativeMapByID"}`][creativeKey];
                    }
                    setBulkEditCreatives(newBulkEditCreatives);
                  } else {
                    setSelectedCreativeKey(
                      selectedCreativeKey === creativeKey ? undefined : creativeKey
                    );
                  }
                }}
              >
                {bulkEditSelect && (
                  <div className="bulkEditSelector">
                    {bulkEditCreatives[creativeKey] ? <MdCheckBox /> : <MdOutlineSquare />}
                  </div>
                )}
                <div className="thumbnailContainer">
                  {getThumbnail({
                    company,
                    creativeKey,
                    creativeMap: filteredCreativeMapByKey,
                    selectedMediatype,
                  })}
                </div>
                {getDescriptionContainer({
                  creativeKey,
                  creativeMap: filteredCreativeMapByKey,
                  isInternal,
                  selectedMediatype,
                  selectedView,
                  setSelectedEditCreative,
                })}
                {selectedView !== VIEW_TYPES.CREATIVE_VIEW &&
                  getABCDScoreContainer({
                    abcdScoreMap: R.defaultTo({}, abcdScoreMap),
                    creativeKey,
                    creativeMap: filteredCreativeMapByKey,
                    runModalAvailable: isInternal,
                    setSelectedABCDCreative,
                  })}
              </div>
            ),
            R.keys(filteredCreativeMapByKey)
          )}
        </div>
        {selectedView !== VIEW_TYPES.CREATIVE_VIEW && (
          <div className="creativeScoreExplanationContainer">
            <div className="explanationContainer">
              <div className="title">Best Practice Score</div>
              <div className="description">
                Your Best Practice Score shows how closely your videos follow Google’s creative best
                practices: grabbing attention, integrating branding, building emotional connection,
                and providing clear direction.
              </div>
              <div className="googleInfoContainer">
                <div className="googleInfo">
                  Using{" "}
                  <a
                    className="googleInfoLink"
                    href="https://business.google.com/us/think/future-of-marketing/youtube-video-ad-creative/"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    research
                  </a>{" "}
                  by:
                </div>
                <Img src={"https://cdn.blisspointmedia.com/assets/img/Google.png"} />
              </div>
            </div>
            <div className="overallScoreContainer">
              <div className="label">Overall Score</div>
              <div className="scoreContainer">
                {overallTaggingScore ? (
                  <RadialScoreChart
                    barSizeRatio={OVERALL_RADIAL_BAR_RATIO}
                    score={overallTaggingScore}
                    size={OVERALL_RADIAL_SIZE}
                  />
                ) : (
                  "N/A"
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {selectedCreativeKey && filteredCreativeMapByKey[selectedCreativeKey] && (
        <div className="selectedCreativeDetailsContainer">
          {getExpandedDetailsContainer({
            abcdCreativeSettingsMap: R.defaultTo({}, abcdCreativeSettingsMap),
            abcdScoreMap: R.defaultTo({}, abcdScoreMap),
            company: companyInfo.cid,
            creativeKey: selectedCreativeKey,
            creativeMap: filteredCreativeMapByKey,
            openABCDSection,
            selectedMediatype: selectedMediatype,
            selectedView,
            setOpenABCDSection,
          })}
          {isInternal &&
            selectedView === VIEW_TYPES.CREATIVE_ID_VIEW &&
            selectedMediatype !== CREATIVE_MEDIATYPES_TYPES.ALL_CREATIVES && (
              <div
                className="editCreativeContainer"
                onClick={() => setSelectedEditCreative(filteredCreativeMap[selectedCreativeKey])}
              >
                Edit {assetLabel}{" "}
                <div className="editIcon">
                  <MdEdit />{" "}
                </div>
              </div>
            )}
        </div>
      )}
    </div>
  );

  const tableView = showTableView && (
    <div className="creativeTableViewContainer">
      <BPMTable
        data={R.values(filteredCreativeMapByKey)}
        headers={getTableViewHeaders({
          creativeOptions: creativeOptions as { categoryData: { name: string }[] },
          selectedMediatype,
          selectedView,
        })}
        noRowsRenderer={() => <div className="noRows">Add creatives</div>}
        filterBar={false}
      />
    </div>
  );

  return (
    <div className="creativeList">
      {runABCDModal}
      {addCreativeModal}
      {editCreativeModal}
      <div className="mediatypeNav">
        {R.map(
          mediatype => (
            <div
              className={`mediatype ${selectedMediatype === mediatype ? "selected" : ""}`}
              onClick={() => {
                setSelectedMediatype(mediatype);
                setSelectedCreativeKey(undefined);
              }}
            >
              {mediatype}
            </div>
          ),
          CREATIVE_HEADERS
        )}
      </div>
      <div className="creativeListContentContainer">
        <div className="creativeListContent">
          <div className="headerContainer">
            <div className="headerControls">
              <div className="viewPickerContainer">
                <Dropdown
                  onChange={view => setSelectedView(view)}
                  options={R.map(
                    view => ({
                      label:
                        view === VIEW_TYPES.CREATIVE_ID_VIEW && assetLabel === "ISCI"
                          ? "ISCI View"
                          : view,
                      value: view,
                    }),
                    R.filter(
                      view => view !== VIEW_TYPES.CREATIVE_ID_VIEW || isInternal,
                      VIEWS
                    ) as View[]
                  )}
                  size={"lg"}
                  type={DropdownToggleType.WIDGET_TITLE}
                  value={selectedView}
                />
              </div>
              {isInternal && selectedMediatype !== CREATIVE_MEDIATYPES_TYPES.ALL_CREATIVES && (
                <div className="controls">
                  {bulkEditSelect && !R.isEmpty(bulkEditCreatives) && (
                    <Button
                      icon={<MdEdit />}
                      onClick={() => {
                        const initialKey = R.keys(bulkEditCreatives)[0];
                        setBulkEditCreatives(R.omit([initialKey], bulkEditCreatives));
                        setSelectedEditCreative(bulkEditCreatives[initialKey]);
                        setBulkEditSelect(false);
                      }}
                      size={"sm"}
                      type={ButtonType.FILLED}
                      variant={ButtonFrameworkVariant.LEADING_ICON}
                    >
                      Bulk Edit {R.keys(bulkEditCreatives).length} {assetLabel}s
                    </Button>
                  )}
                  {bulkEditSelect && (
                    <Button
                      icon={<MdCancel />}
                      onClick={() => {
                        setBulkEditSelect(false);
                        setBulkEditCreatives({});
                      }}
                      size={"sm"}
                      type={ButtonType.FILLED}
                      variant={ButtonFrameworkVariant.LEADING_ICON}
                    >
                      Cancel
                    </Button>
                  )}
                  {!bulkEditSelect && selectedView === VIEW_TYPES.CREATIVE_ID_VIEW && (
                    <Button
                      icon={<MdEdit />}
                      onClick={() => {
                        setBulkEditSelect(true);
                        setSelectedCreativeKey(undefined);
                      }}
                      size={"sm"}
                      type={ButtonType.FILLED}
                      variant={ButtonFrameworkVariant.ICON_ONLY}
                    />
                  )}
                  <IconToggleButton
                    onChange={value => setShowTableView(value === "showTable")}
                    options={[
                      { key: "showTable", icon: <MdOutlineTableRows />, label: "Table View" },
                      { key: "showList", icon: <MdOutlineList />, label: "List View" },
                    ]}
                    selectedOption={showTableView ? "showTable" : "showList"}
                    size="sm"
                  />
                  <DownloadToggle size={"sm"} />
                  <Button
                    icon={<MdAdd />}
                    onClick={() => setShowAddCreativeModal(true)}
                    size={"sm"}
                    type={ButtonType.FILLED}
                    variant={ButtonFrameworkVariant.LEADING_ICON}
                  >
                    Create New
                  </Button>
                </div>
              )}
            </div>
            <div className="filterContainer">
              <div className="mediatypeSpecificFilters">{mediatypeFilters}</div>
              <div className="filterBarContainer">
                <FilterBar
                  hasAdvanced
                  onFilter={filter => setCreativeListFilter({ filter })}
                  lines={R.values(creativeMapForFilterBar)}
                  options={
                    creativeMapForFilterBar && !R.isEmpty(creativeMapForFilterBar)
                      ? R.keys(creativeMapForFilterBar[R.keys(creativeMapForFilterBar)[0]])
                      : []
                  }
                />
              </div>
            </div>
          </div>
          {listView}
          {tableView}
        </div>
      </div>
    </div>
  );
};

export default CreativeList;
